import { Injectable } from '@angular/core';
import createAuth0Client, {
	Auth0Client,
	Auth0ClientOptions,
} from '@auth0/auth0-spa-js';
import {
	ComponentController,
	MessagingService,
	filterNull,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import isEmpty from 'lodash-es/isEmpty';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Observable, combineLatest, from, of } from 'rxjs';
import {
	filter,
	finalize,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';

import { CommandEvent } from 'application/common/model/command-event';
import { environment } from 'environments/environment';
import {
	CoachSettingsGeneralExternalConnectionState,
	CoachSettingsGeneralExternalConnectionStore,
} from 'presentation/page/settings/coach-settings/coach-settings-general/coach-settings-external-connection/coach-settings-general-external-connection.store';
import { ExternalCalendarInformationOutput } from 'user/model/external-calendar-information.output';
import { UserNetworkState } from 'user/model/user-network.state';
import { UserNetworkType } from 'user/model/user-network.type';
import { UserApplicationPort } from 'user/port/user-application.port';
import { User } from 'user/user';

@Injectable()
@UntilDestroy()
export class CoachSettingsGeneralExternalConnectionController extends ComponentController<CoachSettingsGeneralExternalConnectionState> {
	public i18n =
		'presentation.app.page.settings.coach.general.externalConnections';
	public userNetworkMicrosoftState$: Observable<string>;
	public userMicrosoftExternalCalendars$: Observable<SelectItem[]>;
	public isActive$: Observable<boolean>;
	public isExpired$: Observable<boolean>;
	public isNotActive$: Observable<boolean>;
	public canDisconnect$: Observable<boolean>;
	public selectedCalendarsId$: Observable<string[]>;
	public canNotSynchronize$: Observable<boolean>;
	public isLoading$: Observable<boolean>;
	public isSynchronizing$!: Observable<boolean>;
	public isReconnecting$!: Observable<boolean>;
	public synchronizeButtonLabel$: Observable<string>;
	public cardHeader$: Observable<string>;
	private _externalCalendars$: Observable<ExternalCalendarInformationOutput[]>;

	constructor(
		store: CoachSettingsGeneralExternalConnectionStore,
		private _translateService: TranslateService,
		private _messagingService: MessagingService,
		private _confirmationService: ConfirmationService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(store);
		this._initObservables();
		this._initListeners();
	}

	private _initObservables() {
		this.userNetworkMicrosoftState$ = this.select('networkState').pipe(
			filterNull(),
			map((state) => state.toLowerCase()),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.cardHeader$ = this.select('networkType').pipe(
			map((networkType) => networkType.toLowerCase()),
			switchMap((networkType) =>
				this._translateService.stream(this.i18n + '.' + networkType + '.header')
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this._externalCalendars$ = this.select('networkType').pipe(
			switchMap((networkType) =>
				this._userApplicationPort.userExternalCalendar(networkType)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.userMicrosoftExternalCalendars$ = this._externalCalendars$.pipe(
			filterNull(),
			map((calendars) =>
				calendars.map(
					(calendar) =>
						({
							label: calendar.externalCalendarName,
							value: calendar.externalCalendarId,
						} as SelectItem)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.selectedCalendarsId$ = this.select('calendarsId').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isActive$ = this.select('networkState').pipe(
			map((state) => state == UserNetworkState.ACTIVE),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isExpired$ = this.select('networkState').pipe(
			map((state) => state == UserNetworkState.EXPIRED),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isNotActive$ = this.select('networkState').pipe(
			map((state) => state == UserNetworkState.NOT_ACTIVE),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.canDisconnect$ = this.select('networkState').pipe(
			map(
				(state) =>
					state == UserNetworkState.ACTIVE || state == UserNetworkState.EXPIRED
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.canNotSynchronize$ = combineLatest(
			this.isActive$,
			this.select('hasSynchronizedCalendars'),
			this.select('calendarsId')
		).pipe(
			map(
				([isActive, hasSynchronizedCalendars, selectedCalendarsId]) =>
					!isActive ||
					!selectedCalendarsId ||
					(!hasSynchronizedCalendars &&
						(!selectedCalendarsId || isEmpty(selectedCalendarsId)))
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isLoading$ = this.selectLoading().pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isSynchronizing$ = this.select('isSynchronizing').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isReconnecting$ = this.select('isReconnecting').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.synchronizeButtonLabel$ = combineLatest([
			this.select('calendarsId'),
			this.select('hasSynchronizedCalendars'),
		]).pipe(
			map(
				([ids, hasSynchronizedCalendars]) =>
					!ids || !isEmpty(ids) || !hasSynchronizedCalendars
			),
			switchMap((canSynchronize) =>
				this._translateService.stream(
					canSynchronize
						? this.i18n + '.synchronize.label'
						: this.i18n + '.desynchronize.label'
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public synchronizeUserNetworkSelectedCalendars(target: EventTarget) {
		const networkType = this.store.getValue().networkType;

		this._confirmationService.confirm({
			message: this._translateService.instant(
				isEmpty(this.store.getValue().calendarsId)
					? this.i18n + '.desynchronizeCalendarsConfirmationMessage'
					: this.i18n + '.synchronizeCalendarsConfirmationMessage'
			),
			acceptLabel: this._translateService.instant('presentation.primeng.yes'),
			rejectLabel: this._translateService.instant('presentation.primeng.no'),
			accept: () => {
				this.store.update({ isSynchronizing: true });

				this._userApplicationPort
					.synchronizeUserNetworkSelectedCalendars({
						selectedCalendarsId: this.store.getValue().calendarsId,
						networkType: networkType,
					})
					.pipe(
						tap(() => {
							if (!isEmpty(this.store.getValue().calendarsId))
								this._messagingService.publishMessage({
									summaryKey: this.i18n + '.synchronizing.summary',
									detailKey: this.i18n + '.synchronizing.detail',
									severity: 'info',
								});
						}),
						tap(() =>
							this.store.update({
								isSynchronizing: false,
								hasSynchronizedCalendars: !isEmpty(this.store.getValue().calendarsId),
							})
						),
						untilDestroyed(this)
					)
					.subscribe();
			},
			icon: 'pi pi-exclamation-triangle',
			target,
		});
	}

	public reconnectToUserNetwork() {
		this.store.update({ isReconnecting: true });

		const networkType = this.store.getValue().networkType;

		const scope = 'Calendars.ReadWrite offline_access';

		this._userApplicationPort
			.reconnectToUserNetwork({
				networkType: networkType,
				networkRefreshTokenScope: scope,
			})
			.pipe(
				tap(() => this.store.update({ isReconnecting: false })),
				untilDestroyed(this)
			)
			.subscribe();
	}

	public disconnectFromUserNetwork() {
		this.store.setLoading(true);

		const networkType = this.store.getValue().networkType;

		this._userApplicationPort
			.disconnectFromUserNetwork(networkType)
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.store.update({ calendarsId: null, hasSynchronizedCalendars: false });

				this.store.setLoading(false);
			});
	}

	public connectToUserNetwork(): void {
		this.store.setLoading(true);

		const networkType = this.store.getValue().networkType;

		let auth0ClientOptions;
		let scope: string;

		switch (networkType) {
			case 'GOOGLE':
				scope = 'https://www.googleapis.com/auth/calendar';
				auth0ClientOptions = {
					redirect_uri: `${window.location.origin}`,
					client_id: environment.auth0.clientId,
					domain: environment.auth0.domain,
					connection_scope: scope,
					connection: 'google-oauth2',
					access_type: 'offline',
					approval_prompt: 'force',
				} as Auth0ClientOptions;
				break;
			case 'OUTLOOK':
				scope = 'Calendars.ReadWrite offline_access';
				auth0ClientOptions = {
					redirect_uri: `${window.location.origin}`,
					client_id: environment.auth0.clientId,
					domain: environment.auth0.domain,
					prompt: 'select_account',
					connection_scope: scope,
					connection: 'windowslive',
				} as Auth0ClientOptions;
				break;
		}

		if (!auth0ClientOptions) {
			return;
		}

		from(createAuth0Client(auth0ClientOptions))
			.pipe(
				filterNull(),
				tap(() => this.store.setLoading(false)),
				switchMap((client) =>
					from(
						client.loginWithPopup({
							ui_locales: this._translateService.currentLang,
						})
					).pipe(
						tap(() => this.store.setLoading(true)),
						switchMap((_) => client.getUser()),
						map((user) => user.sub),
						filterNull(),
						switchMap((userId) =>
							this._userApplicationPort.connectToUserNetwork({
								auth0UserId: userId,
								networkType: networkType,
								networkRefreshTokenScope: scope,
							})
						)
					)
				),
				tap(() => this.store.setLoading(false)),
				untilDestroyed(this)
			)
			.subscribe();
	}

	public updateCalendarsId(calendarsId: string[]) {
		this.store.update({ calendarsId });
	}

	private _initListeners() {
		combineLatest([
			this.select('networkType').pipe(filterNull()),
			this._userApplicationPort.commandHandled$.pipe(
				filter(
					(e) =>
						e.method === 'connectToUserNetwork' ||
						e.method === 'reconnectToUserNetwork' ||
						e.method === 'disconnectFromUserNetwork'
				),
				startWith(null as CommandEvent<unknown>)
			),
			this._userApplicationPort.observeWebsocketChanges(),
		])
			.pipe(
				switchMap(([networkType]) =>
					this._userApplicationPort.userNetworkState(networkType)
				),
				tap((state) => this.store.update({ networkState: state })),
				untilDestroyed(this)
			)
			.subscribe();

		this._userApplicationPort.commandHandled$
			.pipe(
				filter(
					(e) =>
						e.method === 'connectToUserNetwork' ||
						e.method === 'reconnectToUserNetwork' ||
						e.method === 'disconnectFromUserNetwork'
				),
				startWith(null as CommandEvent<unknown>)
			)
			.pipe(
				switchMap(() => this.isActive$),
				switchMap((isActive) => (isActive ? this._externalCalendars$ : of(null))),
				map((calendars) => {
					return calendars
						? calendars
								.filter((calendar) => calendar.isSelected)
								.map((calendar) => calendar.externalCalendarId)
						: null;
				}),
				tap((ids) =>
					this.store.update({
						calendarsId: ids,
						hasSynchronizedCalendars: !ids || !isEmpty(ids),
					})
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	public updateUserNetworkType(networkType: UserNetworkType) {
		this.store.update({ networkType: networkType });
	}
}
